import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';

/* ======================== SCROLL TO TOP ======================== */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ======================== NAVBAR ======================== */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-inner">
          <Link to="/" className="nav-logo" onClick={closeMobile}>
            <img src="/logo.png" alt="StockAurBill" />
          </Link>
          <ul className="nav-links desktop-only">
            <li><NavLink to="/" end>Home</NavLink></li>
            <li><NavLink to="/features">Features</NavLink></li>
            <li><NavLink to="/pricing">Pricing</NavLink></li>
            <li><NavLink to="/about">About Us</NavLink></li>
            <li><NavLink to="/contact">Contact</NavLink></li>
            <li><a href="https://panel.stockaurbill.com" className="nav-cta">Get Started</a></li>
          </ul>
          <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">&#9776;</button>
        </div>
      </nav>
      {mobileOpen && (
        <div className="mobile-overlay" onClick={closeMobile}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <Link to="/" className="nav-logo" onClick={closeMobile}><img src="/logo.png" alt="StockAurBill" /></Link>
              <button className="mobile-close-btn" onClick={closeMobile}>&times;</button>
            </div>
            <div className="mobile-menu-links">
              <NavLink to="/" end onClick={closeMobile} className="mobile-link">Home</NavLink>
              <NavLink to="/features" onClick={closeMobile} className="mobile-link">Features</NavLink>
              <NavLink to="/pricing" onClick={closeMobile} className="mobile-link">Pricing</NavLink>
              <NavLink to="/about" onClick={closeMobile} className="mobile-link">About Us</NavLink>
              <NavLink to="/contact" onClick={closeMobile} className="mobile-link">Contact</NavLink>
              <a href="https://panel.stockaurbill.com" onClick={closeMobile} className="mobile-cta-btn">Get Started</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ======================== FOOTER ======================== */
function Footer() {
  return (
    <footer className="footer">
      <div className="container-wide">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src="/logo.png" alt="StockAurBill" />
            <p>India's smartest billing, inventory management, and CRM platform built for small & medium businesses. Manage your entire business from one powerful dashboard.</p>
          </div>
          <div className="footer-col">
            <h4>Product</h4>
            <ul>
              <li><Link to="/features">Features</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/features">GST Billing</Link></li>
              <li><Link to="/features">Inventory Management</Link></li>
              <li><Link to="/features">CRM & Sales</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li>📧 info@stockaurbill.com</li>
              <li>🏢 Helcon Technologies Pvt Ltd</li>
              <li>📍 Rohini, New Delhi</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          © 2026 StockAurBill by Helcon Technologies Private Limited. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
}

/* ======================== HOME PAGE ======================== */
function Home() {
  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="container-wide">
          <div className="hero-inner">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="pulse"></span>
                Special Offer — ₹1,800/year (+ GST)
              </div>
              <h1>
                Billing, Inventory &<br />
                CRM — <span className="text-blue">All in One</span><br />
                <span className="text-orange">Powerful Platform</span>
              </h1>
              <p className="hero-sub">
                StockAurBill helps Indian businesses create GST invoices, manage inventory in real‑time, track sales pipelines, and send payment reminders — all from a single dashboard. No accounting knowledge needed.
              </p>
              <div className="hero-buttons">
                <a href="https://panel.stockaurbill.com" className="btn-primary">Get Started →</a>
                <Link to="/features" className="btn-outline">Explore Features</Link>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <div className="num">7 Days</div>
                  <div className="label">Free Trial</div>
                </div>
                <div className="hero-stat">
                  <div className="num">5 Bills</div>
                  <div className="label">Free to Create</div>
                </div>
                <div className="hero-stat">
                  <div className="num">100%</div>
                  <div className="label">GST Compliant</div>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <img src="/hero-poster.jpg" alt="StockAurBill - Handle Your Stock Like A Pro" style={{ width: '100%', maxWidth: '520px', borderRadius: '0px', boxShadow: 'none', background: 'transparent' }} />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="trust-bar">
        <div className="container-wide">
          <div className="trust-inner">
            <div className="trust-item">
              <div className="trust-icon blue">🛡️</div>
              <div className="trust-text">
                <div className="t-num">100% Secure</div>
                <div className="t-label">Data stored in India</div>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon green">⚡</div>
              <div className="trust-text">
                <div className="t-num">GST Ready</div>
                <div className="t-label">Fully compliant invoices</div>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon orange">📱</div>
              <div className="trust-text">
                <div className="t-num">Multi-Device</div>
                <div className="t-label">Mobile & Desktop</div>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon blue">☁️</div>
              <div className="trust-text">
                <div className="t-num">Cloud Based</div>
                <div className="t-label">Access from anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE MODULES */}
      <section className="section">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">Core Modules</span>
            <h2>Everything Your Business Needs in One Place</h2>
            <p>From creating GST bills to managing stock and tracking sales — StockAurBill covers every aspect of your daily business operations.</p>
          </div>

          {/* MODULE 1 - BILLING */}
          <div className="feature-module">
            <div className="feature-text">
              <span className="tag tag-blue">GST Billing</span>
              <h3>Create Professional GST Bills in Seconds</h3>
              <p>
                StockAurBill makes billing effortless. Generate fully GST-compliant invoices, purchase bills, sale bills, quotations, estimates, delivery challans, credit notes, and debit notes — all in just a few clicks. Share invoices via WhatsApp, email, or print them directly using thermal or regular printers. No accounting knowledge required.
              </p>
              <p>
                Our billing software automatically calculates CGST, SGST, and IGST based on your customer's state. Track payment status for every invoice and send automatic payment reminders to recover dues faster. With multiple professional invoice templates to choose from, your bills will always look polished and trustworthy.
              </p>
              <ul className="feature-list">
                <li>GST invoices with auto tax calculation (CGST/SGST/IGST)</li>
                <li>Sale bills, purchase bills, quotations & estimates</li>
                <li>Delivery challans and credit/debit notes</li>
                <li>Share bills via WhatsApp, email, or print</li>
                <li>Multiple professional invoice templates</li>
                <li>Payment reminders via WhatsApp & SMS</li>
                <li>Bill-wise payment tracking and due-date alerts</li>

              </ul>
            </div>
            <div className="feature-visual">
              <div className="feature-card">
                <div className="feature-card-header">
                  <div className="feature-card-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>📄</div>
                  <span className="feature-card-title">Recent Invoices</span>
                </div>
                <table className="mini-table">
                  <thead>
                    <tr><th>Invoice</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>#INV-1247</td><td>Sharma Traders</td><td>₹18,500</td><td><span className="status-badge status-green">Paid</span></td></tr>
                    <tr><td>#INV-1248</td><td>Patel Enterprises</td><td>₹32,100</td><td><span className="status-badge status-orange">Pending</span></td></tr>
                    <tr><td>#INV-1249</td><td>Kumar & Sons</td><td>₹7,200</td><td><span className="status-badge status-green">Paid</span></td></tr>
                    <tr><td>#INV-1250</td><td>Delhi Wholesale</td><td>₹55,800</td><td><span className="status-badge status-blue">Partial</span></td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* MODULE 2 - INVENTORY */}
          <div className="feature-module reverse">
            <div className="feature-text">
              <span className="tag tag-green">Inventory Management</span>
              <h3>Track Every Item Across Multiple Warehouses</h3>
              <p>
                Never run out of stock again. StockAurBill provides real-time inventory tracking with automatic stock adjustment on every sale and purchase. Set low-stock alerts to get notified before items run out, manage products with barcode scanning, and track inventory across multiple godowns or warehouse locations.
              </p>
              <p>
                Whether you manage 50 items or 50,000+ SKUs, our inventory management system scales with your business. Record items by batch number, expiry date, manufacturing date, MRP, and more. Get detailed stock reports including stock summary, item-wise reports, and stock valuation to make smarter purchasing decisions and maximize your warehouse space.
              </p>
              <ul className="feature-list">
                <li>Real-time stock tracking with auto-adjustment on sale/purchase</li>
                <li>Low stock alerts — never miss a reorder point</li>
                <li>Multi-godown & multi-warehouse management</li>

                <li>Track by batch number, expiry date, MRP & more</li>
                <li>Product catalogue with category & sub-category support</li>
                <li>Stock summary, item-wise, and valuation reports</li>
                <li>Bulk import products via Excel/CSV</li>
              </ul>
            </div>
            <div className="feature-visual">
              <div className="feature-card">
                <div className="feature-card-header">
                  <div className="feature-card-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>📦</div>
                  <span className="feature-card-title">Stock Overview</span>
                </div>
                <table className="mini-table">
                  <thead>
                    <tr><th>Item</th><th>Stock</th><th>Value</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Wireless Mouse</td><td>324 pcs</td><td>₹1,94,400</td><td><span className="status-badge status-green">In Stock</span></td></tr>
                    <tr><td>USB-C Cable</td><td>12 pcs</td><td>₹3,600</td><td><span className="status-badge status-orange">Low Stock</span></td></tr>
                    <tr><td>Bluetooth Speaker</td><td>158 pcs</td><td>₹3,16,000</td><td><span className="status-badge status-green">In Stock</span></td></tr>
                    <tr><td>Laptop Stand</td><td>3 pcs</td><td>₹4,500</td><td><span className="status-badge" style={{ background: '#fef2f2', color: '#ef4444' }}>Critical</span></td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* MODULE 3 - CRM */}
          <div className="feature-module">
            <div className="feature-text">
              <span className="tag tag-orange">CRM & Sales Pipeline</span>
              <h3>Turn Leads Into Paying Customers — Faster</h3>
              <p>
                StockAurBill's built-in CRM helps you capture leads, track them through customizable deal stages, set follow-up reminders, and close more deals. Never lose a potential customer because of forgotten follow-ups. Whether leads come from WhatsApp, phone calls, walk-ins, or online inquiries, manage everything in one pipeline view.
              </p>
              <p>
                Assign leads to team members, add notes for each interaction, track deal values, and get insights into your sales performance. Our CRM is designed specifically for Indian SMBs who need a simple, powerful tool to grow their customer base without the complexity of expensive enterprise software. See exactly where each deal stands and what action is needed next.
              </p>
              <ul className="feature-list">
                <li>Visual sales pipeline with drag-and-drop deal stages</li>
                <li>Lead capture from WhatsApp, phone, walk-in & web</li>
                <li>Follow-up reminders so you never miss a callback</li>
                <li>Assign leads to team members for accountability</li>
                <li>Add notes, call logs, and interaction history per lead</li>
                <li>Deal value tracking and revenue forecasting</li>
                <li>Sales performance reports and conversion analytics</li>
                <li>Custom deal stages tailored to your business workflow</li>
              </ul>
            </div>
            <div className="feature-visual">
              <div className="feature-card">
                <div className="feature-card-header">
                  <div className="feature-card-icon" style={{ background: '#fff7ed', color: '#f97316' }}>🎯</div>
                  <span className="feature-card-title">Sales Pipeline</span>
                </div>
                <table className="mini-table">
                  <thead>
                    <tr><th>Lead</th><th>Value</th><th>Stage</th><th>Next Action</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Rajesh Gupta</td><td>₹85,000</td><td><span className="status-badge status-blue">Proposal</span></td><td>Call Tuesday</td></tr>
                    <tr><td>Priya Singh</td><td>₹1,20,000</td><td><span className="status-badge status-orange">Negotiation</span></td><td>Send quote</td></tr>
                    <tr><td>Amit Patel</td><td>₹45,000</td><td><span className="status-badge status-green">Won</span></td><td>Deliver goods</td></tr>
                    <tr><td>Sneha Reddy</td><td>₹2,30,000</td><td><span className="status-badge status-blue">Follow-up</span></td><td>Demo Friday</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MORE FEATURES GRID */}
      <section className="section section-alt">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">Powerful Features</span>
            <h2>Loaded with Features That Indian Businesses Actually Need</h2>
            <p>StockAurBill isn't just billing software — it's your complete business management toolkit, built for how Indian businesses really work.</p>
          </div>
          <div className="features-grid">
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>📊</div>
              <h4>Business Reports</h4>
              <p>Generate profit & loss, balance sheet, GST reports (GSTR-1, GSTR-3B), sales reports, purchase reports, stock reports, expense reports, and party-wise statements — all exportable as PDF or Excel.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>💰</div>
              <h4>Payment Tracking & Reminders</h4>
              <p>Track all receivables and payables from one dashboard. Send payment reminders to customers via WhatsApp and SMS. Never forget who owes you money — StockAurBill keeps a complete ledger for every party.</p>
            </div>

            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fef3c7', color: '#d97706' }}>📱</div>
              <h4>Multi-Device Access</h4>
              <p>Use StockAurBill on your phone, tablet, or desktop — your data syncs across all devices in real time. Start a bill on your phone and finish it on your computer. Access your business anytime, anywhere.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>👥</div>
              <h4>Multi-User & Staff Management</h4>
              <p>Add team members with different access levels. Assign roles — billing staff, warehouse manager, sales team. Control who can see what data. Perfect for growing businesses with multiple employees.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fce7f3', color: '#db2777' }}>🔐</div>
              <h4>Secure Data Backup</h4>
              <p>Your business data is encrypted and stored securely in Indian servers. Automatic daily backups ensure you never lose any data. Restore your complete business information anytime with one click.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>📝</div>
              <h4>Estimates & Quotations</h4>
              <p>Send professional quotations and estimates to your customers in seconds. Convert accepted quotations directly into sale invoices — no need to re-enter any data. Track quotation status and follow up easily.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>📋</div>
              <h4>Expense Tracking</h4>
              <p>Record both GST and non-GST business expenses. Categorize expenses for better analysis. Track recurring expenses and get monthly summaries. Understand where your money goes to optimize spending.</p>
            </div>

          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Simple Process</span>
            <h2>Get Started in 3 Easy Steps</h2>
            <p>Setting up StockAurBill for your business takes less than 5 minutes. No complex setup. No training needed.</p>
          </div>
          <div className="steps-row">
            <div className="step-card">
              <div className="step-num">1</div>
              <h4>Sign Up Free</h4>
              <p>Create your account in 30 seconds. No credit card required. Get instant access to all features during your 7-day free trial.</p>
            </div>
            <div className="step-card">
              <div className="step-num">2</div>
              <h4>Add Your Products</h4>
              <p>Add your inventory items, set prices, GST rates, and start creating professional invoices. Import products in bulk via Excel or CSV.</p>
            </div>
            <div className="step-card">
              <div className="step-num">3</div>
              <h4>Manage & Grow</h4>
              <p>Create bills, track inventory, manage leads, send payment reminders, and grow your business — all from one dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT FOR EVERY INDUSTRY */}
      <section className="section section-alt">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">Industry Solutions</span>
            <h2>Built for Every Type of Indian Business</h2>
            <p>Whether you run a kirana store or a wholesale distribution house, StockAurBill adapts to your industry's unique needs.</p>
          </div>
          <div className="industry-grid">
            <div className="industry-card">
              <div className="industry-icon">🏪</div>
              <h4>Retail Stores</h4>
              <p>Create fast bills at the counter, manage stock across shelves, and track daily sales. Perfect for kirana stores, electronics shops, and general stores.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">🏭</div>
              <h4>Wholesalers & Distributors</h4>
              <p>Manage bulk orders, track large inventories, handle party-wise credit, and generate delivery challans for every consignment efficiently.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">💊</div>
              <h4>Pharmacies</h4>
              <p>Track medicines by batch number and expiry date. Get expiry alerts before items go bad. Generate pharmacy-specific billing formats with ease.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">👗</div>
              <h4>Fashion & Lifestyle</h4>
              <p>Manage apparel, footwear, and accessories inventory by size, color, and variant. Create stylish invoices that match your brand identity.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">🏗️</div>
              <h4>Construction & Hardware</h4>
              <p>Track building materials, cement, steel, and hardware items across sites. Manage supplier payments and purchase orders efficiently.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">🍽️</div>
              <h4>Restaurants & Cafes</h4>
              <p>Quick thermal bill printing, ingredient stock tracking, daily sales reports, and expense management — everything a food business needs.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">💻</div>
              <h4>IT & Services</h4>
              <p>Send professional quotations, track project-based deals in the CRM, manage client relationships, and generate service invoices quickly.</p>
            </div>
            <div className="industry-card">
              <div className="industry-icon">🚀</div>
              <h4>Startups & SMEs</h4>
              <p>Affordable business management for growing companies. Start with billing, add inventory and CRM as your team scales. All at ₹1,800/year.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">Testimonials</span>
            <h2>Trusted by Growing Indian Businesses</h2>
            <p>See what business owners across India are saying about StockAurBill.</p>
          </div>
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"StockAurBill transformed how I manage my electronics shop. Creating GST bills used to take 10 minutes — now it takes 30 seconds. The inventory alerts saved me from losing sales multiple times."</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">RS</div>
                <div>
                  <div className="testimonial-name">Ramesh Sharma</div>
                  <div className="testimonial-role">Electronics Store, Delhi</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"As a wholesale distributor, I needed something to track 5,000+ items across two godowns. StockAurBill handles it perfectly. The party-wise ledger and payment reminders helped me recover ₹2 lakh in pending dues."</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">AP</div>
                <div>
                  <div className="testimonial-name">User</div>
                  <div className="testimonial-role">Wholesale Distributor, Ahmedabad</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p>"The CRM feature is a game-changer. I used to track leads in a diary — now everything is organized with reminders. I've closed 40% more deals since switching to StockAurBill. Highly recommended!"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">PS</div>
                <div>
                  <div className="testimonial-name">Priya Singh</div>
                  <div className="testimonial-role">IT Services, Bangalore</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APP COMING SOON */}
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container-wide">
          <div className="app-banner">
            <div className="container">
              <div className="app-banner-inner">
                <div>
                  <h2>StockAurBill App — Coming Soon!</h2>
                  <p>Manage your entire business from your pocket. The StockAurBill mobile app is launching soon on both Google Play Store and Apple App Store. Create bills, check inventory, and track leads on the go.</p>
                  <div className="app-stores">
                    <div className="store-btn">
                      <span className="store-icon">▶</span>
                      <div className="store-text">
                        <small>Coming Soon on</small>
                        <span>Google Play</span>
                      </div>
                    </div>
                    <div className="store-btn">
                      <span className="store-icon" style={{ fontSize: "1.2rem" }}>🔵</span>
                      <div className="store-text">
                        <small>Coming Soon on</small>
                        <span>App Store</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Simplify Your Business?</h2>
          <p>Join thousands of Indian businesses who are saving time, reducing errors, and growing faster with StockAurBill. Start your 7-day free trial today — no credit card required.</p>
          <div className="cta-buttons">
            <a href="https://panel.stockaurbill.com" className="btn-white">Get Started →</a>
            <Link to="/contact" className="btn-ghost">Talk to Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ======================== FEATURES PAGE ======================== */
function Features() {
  return (
    <>
      <section className="page-hero">
        <h1>All Features</h1>
        <p>Everything you need to run, manage, and grow your business — in one platform.</p>
      </section>

      <section className="section">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">GST Billing</span>
            <h2>Professional Invoicing Made Simple</h2>
            <p>Create GST-compliant bills, quotations, purchase orders, delivery challans, and more in seconds.</p>
          </div>
          <div className="features-grid">
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>📄</div>
              <h4>GST Invoice Generation</h4>
              <p>Create fully compliant GST invoices with automatic CGST, SGST, and IGST calculation based on your customer's location. Generate professional bills in under 30 seconds. Include your logo, payment terms, and custom fields.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>📋</div>
              <h4>Quotations & Estimates</h4>
              <p>Send professional quotations and proforma invoices to potential customers. Track acceptance status and convert approved quotes to sale invoices with one click — no data re-entry needed.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fff7ed', color: '#f97316' }}>🚚</div>
              <h4>Delivery Challans</h4>
              <p>Create delivery challans for goods dispatched without invoice. Track consignment delivery status. Convert challans to invoices when goods reach the customer. Manage consignor and consignee details easily.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fef3c7', color: '#d97706' }}>📑</div>
              <h4>Purchase & Sale Bills</h4>
              <p>Record both sale and purchase transactions. Auto-adjust inventory on each bill. Track purchase history per supplier and sale history per customer for complete business transparency.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>💳</div>
              <h4>Credit & Debit Notes</h4>
              <p>Issue credit notes for returns and debit notes for additional charges. All notes are linked to the original invoice for easy tracking and clean accounting records.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fce7f3', color: '#db2777' }}>🔔</div>
              <h4>Payment Reminders</h4>
              <p>Send automatic payment reminders via WhatsApp and SMS. Track receivables and payables on your dashboard. Bulk reminder feature lets you remind all pending customers at once.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">Inventory</span>
            <h2>Inventory Management That Works</h2>
            <p>Real-time stock tracking, low-stock alerts, barcode support, and multi-warehouse management.</p>
          </div>
          <div className="features-grid">
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>📦</div>
              <h4>Real-Time Stock Tracking</h4>
              <p>Every sale and purchase automatically adjusts your stock levels. Know exactly what's in stock, what's running low, and what needs to be reordered — at any moment, from any device.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>⚠️</div>
              <h4>Low Stock Alerts</h4>
              <p>Set minimum stock levels for each item. When stock drops below the threshold, get instant alerts so you can place orders before running out. Prevent lost sales due to stockouts.</p>
            </div>

            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fef3c7', color: '#d97706' }}>🏭</div>
              <h4>Multi-Warehouse Support</h4>
              <p>Manage stock across multiple godowns, warehouses, or store locations. Transfer stock between locations and track each warehouse's inventory separately from one dashboard.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>📁</div>
              <h4>Product Catalogue</h4>
              <p>Organize products with categories, sub-categories, and variants (size, color, weight). Add images, descriptions, HSN codes, and GST rates for each product in your catalogue.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fce7f3', color: '#db2777' }}>📊</div>
              <h4>Stock Reports</h4>
              <p>Get detailed stock summary, item-wise movement reports, stock valuation, and low-stock reports. Export everything as PDF or Excel for your records or share with your CA.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-wide">
          <div className="section-header">
            <span className="tag">CRM & Sales</span>
            <h2>Close More Deals, Lose Zero Leads</h2>
            <p>A simple yet powerful CRM designed for Indian businesses to manage leads, follow-ups, and deals.</p>
          </div>
          <div className="features-grid">
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fff7ed', color: '#f97316' }}>🎯</div>
              <h4>Lead Management</h4>
              <p>Capture leads from any source — phone calls, WhatsApp, walk-ins, website. Store all lead details in one place with contact info, requirements, budget, and source tracking.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>📊</div>
              <h4>Visual Sales Pipeline</h4>
              <p>See your entire sales pipeline at a glance. Drag and drop deals through custom stages — New Lead, Contacted, Proposal Sent, Negotiation, Won, Lost. Know exactly where each deal stands.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>⏰</div>
              <h4>Follow-up Reminders</h4>
              <p>Set automatic follow-up reminders for each lead. Get notified when it's time to call back, send a quote, or schedule a meeting. Never lose a deal because of a missed follow-up again.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fef3c7', color: '#d97706' }}>👥</div>
              <h4>Team Assignment</h4>
              <p>Assign leads to different team members. Track each person's performance, conversion rate, and deal pipeline. Know which salesperson is bringing the most revenue.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>📝</div>
              <h4>Interaction History</h4>
              <p>Record every call, meeting, email, and WhatsApp conversation with each lead. Build a complete interaction timeline so anyone on your team can pick up where another left off.</p>
            </div>
            <div className="feature-grid-card">
              <div className="fg-icon" style={{ background: '#fce7f3', color: '#db2777' }}>📈</div>
              <h4>Sales Analytics</h4>
              <p>Track conversion rates, deal values, win/loss ratios, and sales cycle length. Identify bottlenecks in your pipeline and optimize your sales process with data-driven insights.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Experience All Features for Free</h2>
          <p>Start your 7-day free trial today. Create up to 5 bills free. No credit card needed. Cancel anytime.</p>
          <div className="cta-buttons">
            <a href="https://panel.stockaurbill.com" className="btn-white">Get Started →</a>
            <Link to="/pricing" className="btn-ghost">View Pricing</Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ======================== PRICING PAGE ======================== */
function Pricing() {
  return (
    <>
      <section className="page-hero">
        <h1>Simple, Transparent Pricing</h1>
        <p>One plan. All features. No hidden charges. Just ₹1,800/year (+ GST).</p>
      </section>

      <section className="section">
        <div className="container">
          <div className="pricing-wrapper">
            <div className="pricing-card">
              <h3>Free Trial</h3>
              <p className="plan-desc">Try before you buy — no commitment needed.</p>
              <div className="price-row">
                <span className="price-amount">₹0</span>
                <span className="price-period">/7 days</span>
              </div>
              <div style={{ marginBottom: 24 }}></div>
              <ul className="pricing-features">
                <li>7-day full access to all features</li>
                <li>Create up to 5 bills free</li>
                <li>GST invoicing with all templates</li>
                <li>Inventory management</li>
                <li>CRM & lead tracking</li>
                <li>Business reports</li>
                <li>Email support</li>
              </ul>
              <a href="https://panel.stockaurbill.com" className="pricing-btn outline" style={{ display: "block", textAlign: "center" }}>Get Started</a>
            </div>

            <div className="pricing-card featured">
              <div className="pricing-badge">PRO PLAN</div>
              <h3>StockAurBill Pro</h3>
              <p className="plan-desc">Complete business management for growing businesses.</p>
              <div className="price-row">
                <span className="price-amount">₹1,800</span>
                <span className="price-period">/year</span>
              </div>
              <div className="price-save">+ 18% GST Extra</div>
              <ul className="pricing-features">
                <li>Unlimited GST invoices & bills</li>
                <li>Full inventory management</li>
                <li>CRM & sales pipeline</li>
                <li>Business reports</li>
                <li>Multi-user access & staff management</li>
                <li>Payment reminders (WhatsApp & SMS)</li>
                <li>Quotations & estimates</li>
                <li>Delivery challans</li>
                <li>Credit & debit notes</li>
                <li>Expense tracking</li>
                <li>Data backup & security</li>
                <li>Priority phone & email support</li>
              </ul>
              <a href="https://panel.stockaurbill.com" className="pricing-btn primary" style={{ display: "block", textAlign: "center" }}>Get StockAurBill Pro →</a>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 48, color: '#6b7280', fontSize: '0.9rem' }}>
            <p>✓ No hidden charges &nbsp;&nbsp; ✓ Cancel anytime &nbsp;&nbsp; ✓ 7-day full refund policy &nbsp;&nbsp; ✓ GST invoice provided</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="tag">FAQ</span>
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers to the most common questions about StockAurBill.</p>
          </div>
          <FAQ />
        </div>
      </section>
    </>
  );
}

/* ======================== FAQ COMPONENT ======================== */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'What is StockAurBill?', a: 'StockAurBill is an all-in-one billing, inventory management, and CRM software built for Indian small and medium businesses. It helps you create GST-compliant invoices, manage stock, track sales leads, send payment reminders, and generate business reports — all from one platform.' },
    { q: 'Is StockAurBill really free to try?', a: 'Yes! We offer a 7-day free trial where you can access all features. You can also create up to 5 bills completely free. No credit card is required to start your trial.' },
    { q: 'How much does StockAurBill cost?', a: 'StockAurBill Pro costs ₹1,800/year excluding GST. This includes all features, unlimited bills, and priority support.' },
    { q: 'Can I use StockAurBill on my phone?', a: 'Our mobile app is coming soon on both Google Play Store and Apple App Store. Meanwhile, you can access the full web version on your mobile browser — it works great on all screen sizes.' },
    { q: 'Is my business data safe?', a: 'Your data is stored on secure servers in India with bank-grade encryption. We perform automatic daily backups so you never lose any information. Only you can access your business data — even our team cannot view it.' },
    { q: 'Who can use StockAurBill?', a: 'Any business that creates bills or manages inventory can use StockAurBill. It\'s perfect for retailers, wholesalers, distributors, pharmacies, restaurants, IT services, freelancers, startups, and manufacturers across India.' },
    { q: 'Do I need to install anything?', a: 'No installation needed. StockAurBill is a cloud-based software — you just need a web browser and internet connection. Your data syncs across all your devices automatically.' },
    { q: 'What kind of support do you provide?', a: 'We provide email support. You can reach us at info@stockaurbill.com. Our support team is available Monday to Saturday, 10 AM to 7 PM.' },
  ];

  return (
    <div className="faq-list">
      {faqs.map((faq, i) => (
        <div className={`faq-item ${openIndex === i ? 'open' : ''}`} key={i}>
          <button className="faq-question" onClick={() => setOpenIndex(openIndex === i ? null : i)}>
            {faq.q}
            <span className="faq-arrow">▼</span>
          </button>
          <div className="faq-answer">{faq.a}</div>
        </div>
      ))}
    </div>
  );
}

/* ======================== ABOUT PAGE ======================== */
function About() {
  return (
    <>
      <section className="page-hero">
        <h1>About StockAurBill</h1>
        <p>Empowering Indian businesses with smart, affordable technology.</p>
      </section>

      <section className="section">
        <div className="container">
          <div className="about-grid">
            <div>
              <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', marginBottom: 16 }}>Our Story</h2>
              <p style={{ color: '#4b5563', lineHeight: 1.8, marginBottom: 16 }}>
                StockAurBill is a product of <strong>Helcon Technologies Private Limited</strong>, incorporated in 2026 and registered with the Ministry of Corporate Affairs, Government of India (CIN: U62099DL2026PTC433919).
              </p>
              <p style={{ color: '#4b5563', lineHeight: 1.8, marginBottom: 16 }}>
                We saw millions of Indian small business owners struggling with messy registers, manual billing errors, and scattered inventory notes. Existing software was either too expensive, too complex, or not designed for Indian business workflows. That's why we built StockAurBill — a simple, powerful, and affordable platform that brings professional billing, inventory management, and customer relationship management together in one place.
              </p>
              <p style={{ color: '#4b5563', lineHeight: 1.8, marginBottom: 16 }}>
                Our mission is simple: help every Indian business, no matter how small, operate professionally, save time, reduce errors, and grow faster with the right technology. We believe great software should be accessible and affordable for everyone — from a kirana store owner in a small town to a wholesale distributor in a metro city.
              </p>
              <p style={{ color: '#4b5563', lineHeight: 1.8 }}>
                Based in Delhi, our team is building StockAurBill with deep understanding of how Indian businesses work — from GST compliance to WhatsApp-first communication, multi-godown management to thermal printer billing. Every feature is designed keeping the Indian business owner in mind.
              </p>
            </div>
            <div>
              <div className="feature-card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏢</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', marginBottom: 16 }}>Company Details</h3>
                <div style={{ textAlign: 'left', fontSize: '0.9rem', color: '#4b5563', lineHeight: 2 }}>
                  <p><strong>Company:</strong> Helcon Technologies Private Limited</p>
                  <p><strong>CIN:</strong> U62099DL2026PTC433919</p>
                  <p> </p>
                  <p><strong>Address:</strong> Rohini, New Delhi</p>
                  <p><strong>Email:</strong> info@stockaurbill.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="about-values">
          <div className="value-card">
            <div className="value-icon">🎯</div>
            <h4>Our Mission</h4>
            <p>Make professional business management accessible and affordable for every Indian entrepreneur.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">💡</div>
            <h4>Our Vision</h4>
            <p>To become India's most trusted platform for small business billing, inventory, and growth management.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">🤝</div>
            <h4>Our Promise</h4>
            <p>Simple software, honest pricing, real support. No hidden charges, no complicated features — just what your business needs.</p>
          </div>
        </div>
      </section>
    </>
  );
}

/* ======================== CONTACT PAGE ======================== */
function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for contacting us! We will get back to you within 24 hours.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <>
      <section className="page-hero">
        <h1>Contact Us</h1>
        <p>Have questions? We're here to help. Reach out and we'll respond within 24 hours.</p>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-info-icon">📧</div>
                <div>
                  <h4>Email Us</h4>
                  <p>info@stockaurbill.com</p>
                  <p>We reply within 24 hours on business days.</p>
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-info-icon">🏢</div>
                <div>
                  <h4>Office Address</h4>
                  <p>Helcon Technologies Private Limited</p>
                  <p>Rohini, New Delhi, India</p>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', marginBottom: 24 }}>Send us a Message</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Enter your full name" />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" />
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <select required value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })}>
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="pricing">Pricing Question</option>
                      <option value="support">Technical Support</option>
                      <option value="demo">Request a Demo</option>
                      <option value="feedback">Feedback</option>
                      <option value="refund">Refund Request</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea rows={5} required value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us how we can help you..."></textarea>
                </div>
                <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Send Message →</button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ======================== PRIVACY POLICY ======================== */
function Privacy() {
  return (
    <>
      <section className="page-hero">
        <h1>Privacy Policy</h1>
        <p>Last updated: March 2026</p>
      </section>
      <div className="legal-content">
        <p>This Privacy Policy describes how Helcon Technologies Private Limited ("we", "us", "our"), the company behind StockAurBill, collects, uses, and protects your personal information when you use our website (stockaurbill.com, stockaurbill.in) and services.</p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following types of information to provide and improve our services:</p>
        <p><strong>Personal Information:</strong> When you register, we collect your name, email address, phone number, business name, GSTIN (if applicable), and billing address.</p>
        <p><strong>Business Data:</strong> Invoices, inventory records, customer data, sales records, and other business information you enter into StockAurBill.</p>
        <p><strong>Payment Information:</strong> When you subscribe, payment details are processed securely through our payment gateway partner. We do not store your credit/debit card details on our servers.</p>
        <p><strong>Usage Data:</strong> We collect information about how you use StockAurBill, including pages visited, features used, session duration, device type, browser type, and IP address.</p>

        <h2>2. How We Use Your Information</h2>
        <p>We use your information to provide the StockAurBill service, process subscriptions and payments, send transactional communications (invoices, payment confirmations, renewal reminders), provide customer support, improve our product and user experience, send product updates and feature announcements (you can opt out anytime), and comply with legal obligations including GST and tax regulations.</p>

        <h2>3. Data Storage & Security</h2>
        <p>All your data is stored on secure servers located in India. We use industry-standard encryption (SSL/TLS) for all data transmission. Our servers are protected by firewalls, intrusion detection systems, and regular security audits. We perform automatic daily backups to prevent data loss. Access to your data is restricted to authorized personnel only.</p>

        <h2>4. Third-Party Services</h2>
        <p>We use select third-party services for specific functions: payment processing (Razorpay/Cashfree), email delivery, analytics (Google Analytics), and cloud infrastructure. These providers have their own privacy policies and are contractually obligated to protect your data.</p>

        <h2>5. Data Sharing</h2>
        <p>We do not sell, rent, or trade your personal or business data to any third party. We may share data only when required by law (court orders, government requests), with your explicit consent, or with service providers who need it to perform services on our behalf (under strict confidentiality agreements).</p>

        <h2>6. Your Rights</h2>
        <p>You have the right to access, correct, or delete your personal data at any time. You can export your business data from StockAurBill. You can request account deletion by contacting us. You can opt out of marketing communications at any time.</p>

        <h2>7. Cookies</h2>
        <p>Our website uses cookies to maintain your login session, remember your preferences, and analyze site usage. You can control cookies through your browser settings.</p>

        <h2>8. Data Retention</h2>
        <p>We retain your data as long as your account is active. After account deletion, we retain data for 90 days for backup purposes, after which it is permanently deleted.</p>

        <h2>9. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. Significant changes will be communicated via email or in-app notification.</p>

        <h2>10. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <p><strong>Helcon Technologies Private Limited</strong><br />Rohini, New Delhi, India<br />Email: info@stockaurbill.com</p>
      </div>
    </>
  );
}

/* ======================== TERMS & CONDITIONS ======================== */
function Terms() {
  return (
    <>
      <section className="page-hero">
        <h1>Terms & Conditions</h1>
        <p>Last updated: March 2026</p>
      </section>
      <div className="legal-content">
        <p>These Terms and Conditions ("Terms") govern your use of the StockAurBill platform and services provided by Helcon Technologies Private Limited. By accessing or using StockAurBill, you agree to these Terms.</p>

        <h2>1. About the Service</h2>
        <p>StockAurBill is a cloud-based billing, inventory management, and CRM software platform designed for Indian businesses. The service is provided by Helcon Technologies Private Limited (CIN: U62099DL2026PTC433919), registered at Rohini, New Delhi, India.</p>

        <h2>2. Account Registration</h2>
        <p>You must provide accurate and complete information during registration. You are responsible for maintaining the confidentiality of your login credentials. You must be at least 18 years old to create an account. Each account is for a single business entity.</p>

        <h2>3. Subscription & Payment</h2>
        <p>StockAurBill offers a 7-day free trial with access to all features. After the trial, the subscription costs ₹1,800 per year excluding GST. Payment can be made through our supported payment methods. Subscriptions are non-transferable. All prices are inclusive of applicable taxes unless stated otherwise. A GST invoice will be provided for every payment.</p>

        <h2>4. Auto-Renewal</h2>
        <p>Your subscription will not auto-renew. You will receive a reminder before your subscription expires. You can renew manually from your dashboard or contact our support team.</p>

        <h2>5. Refund Policy</h2>
        <p>We offer a full refund if you request it within 7 days of payment. Refund requests can be made via email (info@stockaurbill.com). Refunds will be processed within 5-7 business days to your original payment method. For detailed refund terms, please visit our Refund Policy page.</p>

        <h2>6. Acceptable Use</h2>
        <p>You agree not to use StockAurBill for any unlawful purpose, to upload malicious content or code, to attempt to gain unauthorized access to our systems, to resell or redistribute the service without permission, or to use the platform to send spam or unsolicited communications.</p>

        <h2>7. Data Ownership</h2>
        <p>All business data you enter into StockAurBill (invoices, inventory, customer records, etc.) remains your property. We do not claim ownership of your data. You can export your data at any time.</p>

        <h2>8. Service Availability</h2>
        <p>We strive to maintain 99.9% uptime. However, we may have scheduled maintenance windows or unexpected downtime. We will notify users of planned maintenance in advance whenever possible.</p>

        <h2>9. Account Termination</h2>
        <p>You can cancel your account at any time by contacting us. We may terminate accounts that violate these Terms. Upon termination, your data will be retained for 90 days, after which it will be permanently deleted.</p>

        <h2>10. Limitation of Liability</h2>
        <p>StockAurBill is provided "as is." While we strive for accuracy, we are not liable for any business losses, data inaccuracies, or damages arising from the use of our service. Our total liability is limited to the amount you paid for the service in the last 12 months.</p>

        <h2>11. Governing Law</h2>
        <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Delhi, India.</p>

        <h2>12. Contact</h2>
        <p>For questions regarding these Terms, contact us at:<br /><strong>Helcon Technologies Private Limited</strong><br />Rohini, New Delhi, India<br />Email: info@stockaurbill.com</p>
      </div>
    </>
  );
}

/* ======================== REFUND POLICY ======================== */
function Refund() {
  return (
    <>
      <section className="page-hero">
        <h1>Refund & Cancellation Policy</h1>
        <p>Last updated: March 2026</p>
      </section>
      <div className="legal-content">
        <p>At StockAurBill (operated by Helcon Technologies Private Limited), we want you to be completely satisfied with our service. If you're not happy, we offer a straightforward refund policy.</p>

        <h2>1. Free Trial</h2>
        <p>StockAurBill offers a 7-day free trial with access to all features. During the trial, you can also create up to 5 bills completely free. No credit card is required to start your trial. The free trial allows you to evaluate the product before making any payment.</p>

        <h2>2. Refund Eligibility</h2>
        <p>You are eligible for a <strong>full refund</strong> if you request it within <strong>7 days</strong> of making your payment. No questions asked. After 7 days from the date of payment, refunds will not be issued.</p>

        <h2>3. How to Request a Refund</h2>
        <p>To request a refund, you can email us at <strong>info@stockaurbill.com</strong> with your account details and reason for refund. Please include your registered email address, payment transaction ID, and date of purchase in your request.</p>

        <h2>4. Refund Processing</h2>
        <p>Once your refund request is approved, the refund will be processed within <strong>5-7 business days</strong>. The refund will be credited back to your original payment method (bank account, UPI, card, etc.). You will receive a confirmation email once the refund has been initiated.</p>

        <h2>5. Cancellation</h2>
        <p>You can cancel your subscription at any time by contacting our support team. After cancellation, you will continue to have access to your account until the end of your current billing period. After the billing period ends, your account will be moved to free/limited access. Your data will be retained for 90 days after cancellation, after which it will be permanently deleted.</p>

        <h2>6. Non-Refundable Situations</h2>
        <p>Refunds will not be issued in the following cases: requests made after 7 days of payment, accounts terminated due to violation of Terms & Conditions, or if the service was used extensively during the refund period with clear intent of misuse.</p>

        <h2>7. Contact Us</h2>
        <p>If you have any questions about our Refund & Cancellation Policy, please reach out:</p>
        <p><strong>Helcon Technologies Private Limited</strong><br />Rohini, New Delhi, India<br />Email: info@stockaurbill.com</p>
      </div>
    </>
  );
}

/* ======================== 404 PAGE ======================== */

function NotFound() {
  return (
    <div className="not-found">
      <h1>404</h1>
      <h2>Page Not Found</h2>
      <p>The page you're looking for doesn't exist or has been moved.</p>
      <Link to="/" className="btn-primary">Go to Homepage →</Link>
    </div>
  );
}

/* ======================== APP ======================== */
function App() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund-policy" element={<Refund />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
    </>
  );
}

export default App;
